import type { ShiftCode } from '../engine/types'

export interface ShiftCodeChipProps {
  code: ShiftCode
}

/**
 * The roster's own vocabulary, expanded for anyone who lands on the app
 * without already knowing it — a `title` rather than a visually-hidden span,
 * because it is a supplementary hint on top of text that is already fully
 * present and readable (constraint: colour is never the only signal, and
 * the code itself is never hidden either), not a replacement for it. A
 * visually-hidden expansion would also read out *alongside* the letters to
 * a screen reader on every single chip in a list of many — noisy for a
 * vocabulary paramedics already know from their own roster.
 */
const LONG_LABEL: Record<ShiftCode, string> = {
  AM: 'Morning shift',
  D: 'Day shift',
  PM: 'Afternoon shift',
  N: 'Night shift',
}

/**
 * A shift code as a small coloured badge — AM, D, PM or N.
 *
 * The letters are always rendered as text. Colour (the wash behind them, the
 * left rule and the ink itself) is a second signal layered on top, never the
 * only one — CLAUDE.md's "never show an unexplained warning" and Station
 * Ledger's own "selection is carried by fill and weight, never colour alone"
 * both point the same way: this reads correctly in a screenshot, to a
 * colour-blind paramedic, and to a screen reader.
 */
export function ShiftCodeChip({ code }: ShiftCodeChipProps) {
  return (
    <span className="ss-chip" data-code={code} title={LONG_LABEL[code]}>
      {code}
    </span>
  )
}
